import { computed, inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { AuthService } from '@core/services/auth.service';
import { EventTrackingService } from '@core/services/event-tracking.service';
import { EEventType } from '@enums/event-tracking.enum';
import { AiConnectorDialogComponent } from './ai-connector-dialog.component';
import { EAiConnectorSource, EAiConnectorTab, IAiConnectorDialogData } from './ai-connector-dialog.interface';
import { readAiConnectorStorage, writeAiConnectorStorage } from './ai-connector.util';

const DIALOG_ID = 'ai-connector-dialog';
const TEASER_HIDDEN_STORAGE_KEY_PREFIX = 'aiConnectorTeaserHidden:';
// sessionStorage like EventTrackingService's session id, so "once per browser session" matches one event
// session (per browser tab, survives reloads).
const TEASER_VIEWED_STORAGE_KEY_PREFIX = 'aiConnectorTeaserViewed:';

/**
 * Shared state for the AI connector entry points (profile "AI assistants" box, sidebar teaser) -
 * see docs/features/ai-connector-profile-and-teaser.md. Teaser state is per user id,
 * so a different user on the same browser starts fresh.
 */
@Injectable({
  providedIn: 'root',
})
export class AiConnectorService {
  private readonly dialog = inject(MatDialog);
  private readonly eventTrackingService = inject(EventTrackingService);
  private readonly currentUser = toSignal(inject(AuthService).currentDbUser$, { initialValue: null });

  // Mirrors what was written to storage, so the teaser reacts immediately and still hides for the
  // session when storage is unavailable.
  private readonly hiddenTeaserUserIds = signal<ReadonlySet<string>>(new Set());
  private readonly viewedTeaserUserIds = new Set<string>();

  public readonly teaserVisible = computed(() => {
    const user = this.currentUser();

    return (
      !!user &&
      !user.creatorId &&
      !this.hiddenTeaserUserIds().has(user.id) &&
      readAiConnectorStorage(TEASER_HIDDEN_STORAGE_KEY_PREFIX + user.id) !== 'true'
    );
  });

  public openDialog(source: EAiConnectorSource, tab?: EAiConnectorTab): void {
    this.dismissTeaser();

    if (this.dialog.getDialogById(DIALOG_ID)) {
      return;
    }

    this.dialog.open<AiConnectorDialogComponent, IAiConnectorDialogData>(AiConnectorDialogComponent, {
      autoFocus: false,
      data: { source, tab },
      id: DIALOG_ID,
      maxHeight: '90vh',
      maxWidth: 'calc(100vw - 32px)',
      width: '576px',
    });
  }

  public dismissTeaser(): void {
    const userId = this.currentUser()?.id;

    if (!userId) {
      return;
    }

    this.hiddenTeaserUserIds.update((userIds) => new Set(userIds).add(userId));
    writeAiConnectorStorage(TEASER_HIDDEN_STORAGE_KEY_PREFIX + userId, 'true');
  }

  public trackTeaserViewed(source: EAiConnectorSource): void {
    const userId = this.currentUser()?.id;

    if (
      !userId ||
      this.viewedTeaserUserIds.has(userId) ||
      readAiConnectorStorage(TEASER_VIEWED_STORAGE_KEY_PREFIX + userId, 'sessionStorage') === 'true'
    ) {
      return;
    }

    this.viewedTeaserUserIds.add(userId);
    writeAiConnectorStorage(TEASER_VIEWED_STORAGE_KEY_PREFIX + userId, 'true', 'sessionStorage');
    this.trackTeaserEvent(EEventType.mcpTeaserViewed, source);
  }

  public trackTeaserDismissed(source: EAiConnectorSource): void {
    this.trackTeaserEvent(EEventType.mcpTeaserDismissed, source);
  }

  // The teaser sits in the global sidebar: without the explicit undefined, an event on a campaign page
  // would get that campaign's id from EventTrackingService.setCampaignContext().
  private trackTeaserEvent(eventType: EEventType, source: EAiConnectorSource): void {
    this.eventTrackingService.track(eventType, { campaignId: undefined, source });
  }
}
